import PeriodPickerToggle from "components/Forms/PeriodPicker/PeriodPickerToggle";
import dayjs, { type Dayjs } from "dayjs";
import { Form, Formik } from "formik";
import type { AssetType, GroupBy, Period } from "kubb";
import type { Company } from "kubb";
import { groupByEnum } from "kubb";
import { periodEnum } from "kubb";
import { isNil } from "lodash-es";
import type React from "react";
import { useState } from "react";
import AutoSubmit from "../../../../../components/Forms/AutoSubmit";
import { inclusiveDatePeriod } from "../../../../../components/Forms/DateTimePicker";
import GroupByRadioButtonGroupField from "../../../../../components/Forms/GroupByPicker/GroupByRadioButtonGroupField";
import Card, { FixHeightCardBody } from "../../../../../components/Theme/card";
import AggregatedStatsChart from "../../../../../components/charts/AggregatedStatsChart";
import TimeSeriesStatsApiWrapper, {
  type AggregatedTimeSeriesStatsParams,
} from "../../../../../containers/report/latestDataReport/TimeSeriesStatsApiWrapper";
import { useSelectedCompany } from "../../../../../hooks/useCompany";
import {
  useAssetPositionGroups,
  useSelectedScheme,
} from "../../../../../hooks/useScheme";
import {
  BillableAssets,
  NonBillableAssetTypes,
} from "../../../../../model/assetPosition";

function periodName(period: Period): string {
  switch (period) {
    case "DAILY":
      return "Daily";
    case "MONTHLY":
      return "Monthly";
    case "WEEKLY":
      return "Weekly";
    case "SEVEN_DAY":
      return "7 Day";
    case "ALL":
      return "All";
    default:
      return "ALL";
  }
}

const monthInclusiveDatePeriod = (length = 3): [Dayjs, Dayjs] => {
  const endDate: Dayjs = dayjs().startOf("day").subtract(1, "day");
  const startDate: Dayjs = dayjs(endDate)
    .subtract(length, "month")
    .startOf("month");
  return [startDate, endDate];
};

const periodLength = (period: Period): [Dayjs, Dayjs] => {
  switch (period) {
    case "MONTHLY":
      return monthInclusiveDatePeriod();
    case "SEVEN_DAY":
      return inclusiveDatePeriod(undefined, undefined, 56);
    default:
      return inclusiveDatePeriod();
  }
};

const DailyReadSuccessWidget = (): React.ReactElement => {
  const selectedCompany = useSelectedCompany() as Company;
  const selectedScheme = useSelectedScheme();
  const assetPositionGroups = useAssetPositionGroups();
  const schemeMode = !isNil(selectedScheme);

  let assetTypes: AssetType[] = Object.values(NonBillableAssetTypes);
  assetTypes = assetTypes.concat(Object.values(BillableAssets));

  // Grouping options depend on the scheme/no-scheme mode (mirrors the radio
  // below): with a scheme → NONE | GROUP_ALL (by asset-position-group); without a
  // scheme → NONE | SCHEME. GROUP_ALL requires a scheme; SCHEME requires none.
  const validGroupBys: GroupBy[] = schemeMode
    ? [groupByEnum.NONE, groupByEnum.GROUP_ALL]
    : [groupByEnum.NONE, groupByEnum.SCHEME];
  const defaultGroupBy = (): GroupBy =>
    schemeMode ? groupByEnum.GROUP_ALL : groupByEnum.SCHEME;

  const [period, setPeriod] = useState<Period>(periodEnum.SEVEN_DAY);
  const [group_by, setGroupBy] = useState<GroupBy>(defaultGroupBy);

  // Clamp the stored choice to the current mode and use this everywhere (API
  // params, chart, form). Deriving it each render — rather than resetting state on
  // a mode flip — is race-free: a stale group_by (e.g. GROUP_ALL kept after the
  // scheme is cleared to "All") never reaches the API, which previously errored.
  // The old layout reset this via an implicit remount; storing it as state made
  // the reset race with AutoSubmit's debounced write-back into the same state.
  const effectiveGroupBy: GroupBy = validGroupBys.includes(group_by)
    ? group_by
    : defaultGroupBy();

  const [start_datetime, end_datetime] = periodLength(period);

  const params: AggregatedTimeSeriesStatsParams = {
    start_datetime,
    end_datetime,
    parameter: "Quality Assessment",
    aggregation: "received_rows_basic_complete",
    company_id: selectedCompany?.company_id || "NONE",
    scheme_id: selectedScheme?.scheme_id,
    group_by: effectiveGroupBy,
    asset_types: assetTypes,
    period: period,
  };

  const periodNameText = periodName(period);

  return (
    <Card
      title={<>Billing Read Success ({periodNameText})</>}
      iconBlock={
        <Formik
          initialValues={{
            groupBy: effectiveGroupBy,
            period: period,
          }}
          onSubmit={(v) => {
            setGroupBy(v.groupBy);
            setPeriod(v.period);
          }}
          enableReinitialize={true}
        >
          {() => (
            <Form>
              <AutoSubmit />
              <div className={"flex justify-end gap-2"}>
                <div>
                  {(!schemeMode || assetPositionGroups) && (
                    <GroupByRadioButtonGroupField
                      fieldName={"groupBy"}
                      noneLabel={schemeMode ? "SCHEME" : "COMPANY"}
                      includeAssetPositionGroup={schemeMode}
                      includeScheme={!schemeMode}
                      size={"sm"}
                      label={{ margin: 0, size: "sm" }}
                    />
                  )}
                </div>
                <div>
                  <PeriodPickerToggle
                    fieldName={"period"}
                    size={"sm"}
                    label={{ margin: 0, size: "sm" }}
                  />
                </div>
              </div>
            </Form>
          )}
        </Formik>
      }
    >
      <FixHeightCardBody height={96} overflowScroll={false}>
        <TimeSeriesStatsApiWrapper request={params}>
          {(chartProps) => (
            <AggregatedStatsChart
              {...chartProps}
              clickUrl={"/admin/report/row_data_quality"}
              groupBy={effectiveGroupBy}
              seriesName={"% of meter points with &gt; 1 read in period "}
              showCountAsPercentage={true}
              units={"%"}
              sharedTooltip={false}
            />
          )}
        </TimeSeriesStatsApiWrapper>
      </FixHeightCardBody>
    </Card>
  );
};

export default DailyReadSuccessWidget;
